/**
 * Local Branch Contact Management Hooks
 *
 * Simplified hooks that use the BranchContext for local state management
 * instead of server-side API calls. Provides CRUD functionality for branch contacts.
 */

import { useBranchContext } from '@/context/BranchContext';
import type { BranchContactFormData } from '@/types/branchContact.types';

/**
 * Hook for getting all branch contacts from local state
 */
export const useLocalBranchContacts = (branchId?: string) => {
  const { branchContacts, getBranchContactsByBranchId, isLoading, error } =
    useBranchContext();

  const filteredContacts = branchId
    ? getBranchContactsByBranchId(branchId)
    : branchContacts;

  return {
    data: { data: filteredContacts },
    isLoading,
    error,
  };
};

/**
 * Hook for getting a single branch contact by ID from local state
 */
export const useLocalBranchContact = (contactId: string | null) => {
  const { getBranchContactById, isLoading, error } = useBranchContext();

  const contact = contactId ? getBranchContactById(contactId) : undefined;

  return {
    data: contact ? { data: contact } : null,
    isLoading,
    error,
  };
};

/**
 * Hook for creating a new branch contact in local state
 */
export const useLocalCreateBranchContact = () => {
  const { addBranchContact, isLoading } = useBranchContext();

  return {
    mutateAsync: addBranchContact,
    isPending: isLoading,
  };
};

/**
 * Hook for updating an existing branch contact in local state
 */
export const useLocalUpdateBranchContact = () => {
  const { updateBranchContact, isLoading } = useBranchContext();

  return {
    mutateAsync: ({ id, data }: { id: string; data: BranchContactFormData }) =>
      updateBranchContact(id, data),
    isPending: isLoading,
  };
};

/**
 * Hook for deleting a branch contact from local state
 */
export const useLocalDeleteBranchContact = () => {
  const { deleteBranchContact, isLoading } = useBranchContext();

  return {
    mutateAsync: deleteBranchContact,
    isPending: isLoading,
  };
};

/**
 * Combined hook for all branch contact operations
 */
export const useLocalBranchContactOperations = () => {
  const context = useBranchContext();

  return {
    // Data
    branchContacts: context.branchContacts,
    isLoading: context.isLoading,
    error: context.error,

    // Operations
    addBranchContact: context.addBranchContact,
    updateBranchContact: context.updateBranchContact,
    deleteBranchContact: context.deleteBranchContact,
    getBranchContactById: context.getBranchContactById,
    getBranchContactsByBranchId: context.getBranchContactsByBranchId,

    // Utilities
    clearError: context.clearError,
  };
};
